/**
 * Persona labels and permission helpers.
 *
 * The permission list comes from the server (`/auth/me`), which derives it from
 * the SRS §5 matrix in `backend/permissions.py`. Nothing here re-implements
 * that table: a browser-side copy would drift, and it is not a security
 * control in any case. These helpers only decide whether to render a control
 * the server would accept.
 */
export const ROLE_LABELS = {
  system_admin: "System Administrator",
  sample_manager: "Sample Manager",
  analyst: "Analyst",
  supervisor: "Supervisor",
  qa: "Quality Assurance",
  client_user: "Client User",
};

export const ROLE_DESCRIPTIONS = {
  system_admin: "Full control of this laboratory, including user administration",
  sample_manager: "Registers samples and owns accessioning, storage and inventory",
  analyst: "Runs assigned tests and records results",
  supervisor: "Assigns work and performs first-level review",
  qa: "Final review and release, audit oversight",
  client_user: "External customer; sees only their own released results",
};

export const ASSIGNABLE_ROLES = [
  "system_admin",
  "supervisor",
  "qa",
  "sample_manager",
  "analyst",
  "client_user",
];

export const roleLabel = (r) =>
  ROLE_LABELS[r] || (r ? r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

/** Read new multi-role sessions and legacy scalar-role sessions uniformly. */
export const rolesForUser = (user) => {
  const stored = Array.isArray(user?.roles) && user.roles.length
    ? user.roles
    : user?.role ? [user.role] : [];
  return [...new Set(stored.filter(Boolean))];
};

export const roleBadgeStyle = (r) => {
  const map = {
    system_admin: "bg-slate-900 text-white",
    supervisor: "bg-blue-50 text-blue-700 border border-blue-200",
    sample_manager: "bg-cyan-50 text-cyan-700 border border-cyan-200",
    analyst: "bg-violet-50 text-violet-700 border border-violet-200",
    qa: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    client_user: "bg-amber-50 text-amber-700 border border-amber-200",
  };
  return map[r] || "bg-slate-100 text-slate-600 border border-slate-200";
};

/** True when the server told us this session holds `action`. */
export const can = (user, action) =>
  Array.isArray(user?.permissions) && user.permissions.includes(action);

export const canDeleteSamples = (u) => can(u, "sample.delete");
export const canRegisterSamples = (u) => can(u, "sample.register");
export const canDeleteTests = (u) => can(u, "test.delete");
export const canAssignTests = (u) => can(u, "test.assign");
export const canEnterResults = (u) => can(u, "result.enter");
export const canDeleteInventory = (u) => can(u, "inventory.delete");
export const canManageInventory = (u) => can(u, "inventory.manage");
export const canManageStorage = (u) => can(u, "storage.manage");
export const canManageClients = (u) => can(u, "client.manage");
export const canManageCatalogue = (u) => can(u, "catalogue.manage");
export const canManageOrders = (u) => can(u, "order.manage");
export const canReleaseResults = (u) => can(u, "result.release");
export const canReviewResults = (u) => can(u, "result.review");
export const canDeleteStorage = (u) => can(u, "storage.delete");
export const canDeleteClients = (u) => can(u, "client.delete");
export const canDeleteOrders = (u) => can(u, "order.delete");
export const canDeleteCatalogue = (u) => can(u, "catalogue.delete");
export const canViewAudit = (u) => can(u, "audit.view");
export const canRevokeCertificates = (u) => can(u, "certificate.revoke");
export const canManageUsers = (u) => can(u, "user.manage");
export const canViewUsers = (u) => can(u, "user.view");
